import { useState, type ReactNode } from 'react';
import { Lightbulb } from 'lucide-react';
import { TASK_SUGGESTION_ACTIONS } from '@shared/suggestion-chat';
import {
  SUGGESTION_ACTIONS,
  SUGGESTION_ACTION_LABELS,
  SUGGESTION_NOTE_MAX,
  suggestionNoteRequired,
  type FactorySuggestion,
  type FactorySuggestions,
  type SuggestionAction,
  type SuggestionDecision,
  suggestionTaskRef,
} from '@shared/factory-suggestions';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DialogLink } from '@/pages/link-dialog/DialogLink';
import { parseTaskNumber } from '@shared/task-number';
import { useFormatTaskRef } from '@/lib/current-project';
import type { TaskRef } from '@shared/types/tasks';
import { useBoardPath } from '@/navigation/project-paths';
import { cn } from '@/lib/utils';
import { isUpstreamSuggestionChoice, sendReplyRead, type UpstreamProposal } from '@shared/upstream-deferral';
import { SendUpstreamButton, SentUpstreamNote, type UpstreamTagging } from './SendUpstreamButton';
import { FoldingCard } from './FoldingCard';
import type { ConcernTags } from '@shared/decision-concern';
import { ConcernBadge } from './ConcernBadge';
import { DecisionItemInput, type DecisionValue } from './DecisionItemInput';

/** What each action does, as its hover text. */
const ACTION_HINTS: Record<SuggestionAction, string> = {
  yes: 'The AI builder builds it on this task’s branch and PR',
  branch: 'A new task for the AI builder, stacked on this task’s branch, with its own PR',
  backlog: 'A task for later: the AI builder makes it from what it knows',
  no: 'Not doing it: recorded as decided',
  ask: 'Ask the AI builder about it first',
};

const ACTION_TONES: Record<SuggestionAction, StatusTone> = {
  yes: 'success',
  branch: 'info',
  backlog: 'neutral',
  no: 'neutral',
  ask: 'warning',
};

/** A decision being made: the action picked and its note. */
export interface SuggestionPick {
  action: SuggestionAction;
  note: string;
}

/**
 * T-58: the run's suggestions out with the task's source, and Jev's picks from the replies there
 * (pre-selected). `onSend` (a mirrored task, someone who can comment): the Send to <source> button.
 */
export interface SuggestionsUpstream {
  sourceLabel: string;
  sent: ReadonlyMap<number, unknown>;
  proposals: ReadonlyMap<number, UpstreamProposal>;
  /** The task still waits on its source; each sent item checks its own send for a reply read. */
  waiting?: boolean;
  onSend?: (numbers: number[], mentionId: string | null) => Promise<boolean>;
  /** SF-T-101: who the send can tag. */
  tagging?: UpstreamTagging;
}

/**
 * T-14: the coding agent's suggestions for further changes (a `factory-suggestions` block in its
 * report), as a card. Someone who can comment picks an action for each suggestion they want to decide
 * (Yes please, New branch, Backlog, No thanks) and applies them together; decided ones show
 * their decision. SF-T-304: `askSlot` puts each suggestion's live chat (SuggestionChat) under it. Without `onApply` (a preview) it only lists the suggestions and decisions.
 * T-58: with `upstream`, suggestions sent to the task's source say so, and the decisions picked from
 * the replies there start selected. SF-T-178: with `onDismiss`, Dismiss says No thanks to every one
 * still open, in one go.
 * SF-T-308: the wrap phase of the one decisions card: each suggestion has the controls every decision has
 * (DecisionItemInput): its options (picking one is Yes please; one with none offers Yes please alone), the Other /
 * steer box (on its own, a different way to do it), Skip (what No thanks was), Backlog and New branch.
 */
export function FactorySuggestionsCard({
  suggestions,
  decided,
  meta,
  onApply,
  onDismiss,
  taskRefs,
  onNavigate,
  upstream,
  onPicksChange,
  actions = TASK_SUGGESTION_ACTIONS,
  hints,
  askSlot,
  from = 'the AI builder',
  fold = false,
  foldDecided = false,
  id,
  concerns,
}: {
  suggestions: FactorySuggestions;
  /**
   * The actions offered, in order: a task's are TASK_SUGGESTION_ACTIONS (SF-T-304: Ask is the chat in `askSlot`
   * there, not a decision); a chat offers Backlog, No thanks and Ask (T-51).
   */
  actions?: readonly SuggestionAction[];
  /** SF-T-304: under each suggestion, its live chat (SuggestionChat), decided or not. */
  askSlot?: (number: number, title: string, decided: boolean) => ReactNode;
  /** Hover text that replaces an action's usual one. */
  hints?: Partial<Record<SuggestionAction, string>>;
  /** Who suggested them, in the header ("the agent" in a chat). */
  from?: string;
  /** Decisions so far, by suggestion number (1-based). */
  decided?: ReadonlyMap<number, SuggestionDecision>;
  /** Which run and when, at the end of the header line. */
  meta?: ReactNode;
  /** Applies the picks; resolves true when they were applied (the picks then clear). */
  onApply?: (decisions: Array<SuggestionPick & { number: number }>) => Promise<boolean>;
  /** SF-T-178: No thanks on every suggestion still open (declineTaskSuggestions); resolves true when done. Leave out where it can't. */
  onDismiss?: () => Promise<boolean>;
  /** The tasks Backlog and New branch made, by number (getTaskRefs): their refs link to them. Unknown refs stay text. */
  taskRefs?: ReadonlyMap<number, TaskRef>;
  /** Called when such a link is followed (closes a pop-up). */
  onNavigate?: () => void;
  upstream?: SuggestionsUpstream;
  /** The suggestions picked and not applied yet, by number, on each change (a dialog asks before losing them). */
  onPicksChange?: (numbers: number[]) => void;
  /** Starts folded to its header line, which opens it (T-72: a decided round in the task's Suggestions). */
  fold?: boolean;
  /** While some are still open, hides the decided ones behind a "Show n decided" toggle (T-72: the task's To answer, the Inbox's Check). */
  foldDecided?: boolean;
  /** For a pointer in the comments to scroll to it (suggestionRoundId). */
  id?: string;
  /** SF-T-182: Jev's Product or Architecture tags for the items (payloadConcerns of the message), a badge on each. */
  concerns?: ConcernTags;
}) {
  // What's picked here; null is one cleared (so a pick from upstream doesn't come back).
  const [local, setPicks] = useState<Record<number, DecisionValue | null>>({});
  const [applying, setApplying] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showDecided, setShowDecided] = useState(false);
  const list = suggestions.suggestions;
  const open = list.filter((_s, i) => !decided?.has(i + 1)).length;
  // An answer (an option, or words) is Yes please, or Ask where there's no Yes please (a chat, T-51).
  const answerAction: SuggestionAction = actions.includes('yes') ? 'yes' : 'ask';
  const values: Record<number, DecisionValue> = {};
  const picks: Record<number, SuggestionPick> = {};
  list.forEach((s, i) => {
    const number = i + 1;
    const proposal = upstream?.proposals.get(number)?.choice;
    const value = number in local ? local[number] : isUpstreamSuggestionChoice(proposal) ? valueOfAction(s, proposal) : null;
    if (!value) return;
    values[number] = value;
    const pick = pickOf(value, answerAction);
    if (pick) picks[number] = pick;
  });
  const decidedCount = list.length - open;
  // Folded away only while something's left to decide: a decided round shows them all.
  const hideDecided = foldDecided && open > 0 && decidedCount > 0 && !showDecided;
  const chosen = Object.entries(picks)
    .map(([n, pick]) => ({ number: Number(n), ...pick }))
    .filter((p) => !decided?.has(p.number));
  const missingNote = chosen.some((p) => suggestionNoteRequired(p.action) && !p.note.trim());
  const sendable = upstream?.onSend ? list.flatMap((s, i) => (decided?.has(i + 1) || upstream.sent.has(i + 1) ? [] : [{ number: i + 1, title: s.title }])) : [];

  const set = (number: number, change: Partial<DecisionValue>) => {
    const value = { ...(values[number] ?? { picked: [], other: '' }), ...change };
    // A pick cleared here is kept as null, so a pick read from upstream doesn't come back.
    const next = { ...local, [number]: pickOf(value, answerAction) || value.other.trim() ? value : null };
    setPicks(next);
    onPicksChange?.(Object.entries(next).flatMap(([n, v]) => (v && pickOf(v, answerAction) ? [Number(n)] : [])));
  };

  const apply = async () => {
    if (!onApply || !chosen.length || missingNote) return;
    setApplying(true);
    const ok = await onApply(chosen.sort((a, b) => a.number - b.number));
    setApplying(false);
    if (ok) {
      setPicks({});
      onPicksChange?.([]);
    }
  };

  const dismiss = async () => {
    if (!onDismiss) return;
    setDismissing(true);
    const ok = await onDismiss();
    setDismissing(false);
    if (ok) {
      setPicks({});
      onPicksChange?.([]);
    }
  };

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={decidedCount ? decisionsSummary(list.length, decided) : undefined}
      header={
        <>
          <Lightbulb className="size-4" aria-hidden />
          {list.length === 1 ? `A suggestion from ${from}` : `${list.length} suggestions from ${from}`}
          {open === 0 ? <Badge variant="secondary">Decided</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-3">
        {list.map((s, i) => {
          const number = i + 1;
          const decision = decided?.get(number);
          if (decision && hideDecided) return null;
          return (
            <li key={s.id} className="space-y-1.5">
              <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                <p className="min-w-0 flex-1 text-sm">
                  <span className="text-muted-foreground">{number}. </span>
                  <span className="font-medium">{s.title}</span> <ConcernBadge concerns={concerns} kind="suggestion" item={s.id} />
                </p>
                {s.recommend && !decision && actions.includes(s.recommend) ? (
                  <Badge variant="ai" size="sm">
                    Recommends: {SUGGESTION_ACTION_LABELS[s.recommend]}
                  </Badge>
                ) : null}
                {decision ? (
                  <StatusBadge tone={ACTION_TONES[decision.action]} size="sm" dot>
                    {SUGGESTION_ACTION_LABELS[decision.action]}
                  </StatusBadge>
                ) : null}
              </div>
              {s.detail ? <p className="whitespace-pre-wrap break-words pl-4 text-xs text-muted-foreground">{s.detail}</p> : null}
              {/* SF-T-300: what building it, or leaving it out, would mean. */}
              {s.change ? (
                <p className="break-words pl-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">If it’s built:</span> {s.change}
                </p>
              ) : null}
              {s.without ? (
                <p className="break-words pl-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Without it:</span> {s.without}
                </p>
              ) : null}
              {upstream && !decision && upstream.sent.has(number) ? (
                <div className="pl-4">
                  <SentUpstreamNote sourceLabel={upstream.sourceLabel} proposal={upstream.proposals.get(number)} waiting={Boolean(upstream.waiting) && !sendReplyRead(upstream.sent.get(number))}>
                    {upstreamChoiceLabel(upstream.proposals.get(number)?.choice)}
                  </SentUpstreamNote>
                </div>
              ) : null}
              {decision ? (
                decision.note ? (
                  <p className="whitespace-pre-line break-words pl-4 text-xs">
                    <span className="text-muted-foreground">{decisionNoteLabel(decision)} </span>
                    <DecisionNote decision={decision} taskRefs={taskRefs} onNavigate={onNavigate} />
                  </p>
                ) : null
              ) : onApply ? (
                <div className="pl-4">
                  <DecisionItemInput
                    label={s.title}
                    options={itemOptions(s, actions)}
                    multiSelect={s.multiSelect}
                    recommended={s.recommended ?? (s.recommend === 'yes' && !s.options?.length ? YES_OPTION : undefined)}
                    value={values[number]}
                    // The note carries the options picked too, so the box leaves them room.
                    maxLength={Math.max(0, SUGGESTION_NOTE_MAX - (values[number]?.picked.join('; ').length ?? 0) - 2)}
                    disabled={applying || dismissing}
                    actions={{ skip: actions.includes('no'), backlog: actions.includes('backlog'), branch: actions.includes('branch') }}
                    hints={{ skip: hints?.no ?? ACTION_HINTS.no, backlog: hints?.backlog ?? ACTION_HINTS.backlog, branch: hints?.branch ?? ACTION_HINTS.branch }}
                    placeholder={
                      answerAction === 'ask'
                        ? 'Ask the agent about it, or say how…'
                        : s.options?.length
                          ? 'Other, or steer it: “this, but …”'
                          : 'Or say how: “yes, but …”, or another way'
                    }
                    onChange={(change) => set(number, change)}
                  />
                </div>
              ) : null}
              {askSlot ? <div className="pl-4">{askSlot(number, s.title, Boolean(decision))}</div> : null}
            </li>
          );
        })}
      </ol>
      {foldDecided && open > 0 && decidedCount > 0 ? (
        <Button variant="link" size="xs" className="text-muted-foreground" aria-expanded={showDecided} onClick={() => setShowDecided(!showDecided)}>
          {showDecided ? 'Hide the decided ones' : `Show ${decidedCount} already decided`}
        </Button>
      ) : null}
      {(onApply || onDismiss) && open > 0 ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {sendable.length && upstream?.onSend ? (
            <span className="mr-auto">
              <SendUpstreamButton sourceLabel={upstream.sourceLabel} items={sendable} what="suggestions" tagging={upstream.tagging} onSend={upstream.onSend} />
            </span>
          ) : null}
          {onApply ? (
            <span className="text-xs text-muted-foreground">
              {missingNote ? 'Write your question to apply' : chosen.length ? `${chosen.length} of ${open} picked` : 'Pick an option, steer, Skip or Backlog on each'}
            </span>
          ) : null}
          {onDismiss ? (
            <Button
              size="sm"
              variant="outline"
              loading={dismissing}
              disabled={applying}
              data-hint={open === 1 ? 'Skip this suggestion' : `Skip the ${open} still open`}
              onClick={() => void dismiss()}
            >
              Dismiss
            </Button>
          ) : null}
          {onApply ? (
            <Button size="sm" loading={applying} disabled={!chosen.length || missingNote || dismissing} onClick={() => void apply()}>
              Apply
            </Button>
          ) : null}
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** Jev's pick as the action's label ("Yes please"). */
function upstreamChoiceLabel(choice: string | undefined): string | undefined {
  return isUpstreamSuggestionChoice(choice) ? SUGGESTION_ACTION_LABELS[choice] : undefined;
}

/** T-72: what was decided, on one line for a folded round: "2 New branch · 1 No thanks". */
function decisionsSummary(total: number, decided?: ReadonlyMap<number, SuggestionDecision>): string | undefined {
  if (!decided?.size) return undefined;
  const counts = new Map<SuggestionAction, number>();
  for (const d of decided.values()) counts.set(d.action, (counts.get(d.action) ?? 0) + 1);
  const parts = SUGGESTION_ACTIONS.filter((a) => counts.has(a)).map((a) => `${counts.get(a)} ${SUGGESTION_ACTION_LABELS[a]}`);
  const left = total - decided.size;
  return [...parts, ...(left > 0 ? [`${left} to decide`] : [])].join(' · ');
}

/** A decision's note; a new task's ref links to that task when it's known. */
function DecisionNote({
  decision,
  taskRefs,
  onNavigate,
}: {
  decision: SuggestionDecision;
  taskRefs?: ReadonlyMap<number, TaskRef>;
  onNavigate?: () => void;
}) {
  const boardPath = useBoardPath();
  const formatRef = useFormatTaskRef();
  const ref = suggestionTaskRef(decision);
  const number = ref ? parseTaskNumber(ref) : null;
  const task = number ? taskRefs?.get(number) : undefined;
  if (!task) return <>{decision.note}</>;
  return (
    // SF-T-160: in the chat it opens in the link dialog, as the chat's other links do.
    <DialogLink to={boardPath(task.id)} onClick={onNavigate} className={cn('font-medium hover:underline', task.done && 'line-through')}>
      {formatRef(task.number)} {task.title}
    </DialogLink>
  );
}

function decisionNoteLabel(decision: SuggestionDecision): string {
  if (decision.action === 'branch' || (decision.action === 'backlog' && suggestionTaskRef(decision))) return 'New task';
  // SF-T-308: a Backlog the AI builder makes: its note is for that task, until it reports what it did.
  if (decision.outcome && 'reason' in decision.outcome) return 'The AI builder made no task:';
  if (decision.action === 'backlog') return 'The AI builder makes the task:';
  if (decision.action === 'ask') return 'Asked:';
  if (decision.action === 'yes') return 'How:';
  return 'Why:';
}

/** SF-T-308: the one option a suggestion without options offers: Yes please. */
const YES_OPTION = SUGGESTION_ACTION_LABELS.yes;

/** SF-T-308: the options a suggestion offers: its own, else Yes please alone (where Yes please is an action). */
function itemOptions(s: FactorySuggestion, actions: readonly SuggestionAction[]) {
  if (s.options?.length) return s.options;
  return actions.includes('yes') ? [{ label: YES_OPTION, description: ACTION_HINTS.yes }] : [];
}

/**
 * SF-T-308: what's picked on a suggestion as the decision it applies: Skip, Backlog and New branch with the box as
 * their note; an option or words are `answer` (Yes please; Ask in a chat), its note the options picked (not the
 * bare Yes please) and the steer. Null when nothing is picked yet.
 */
function pickOf(value: DecisionValue, answer: SuggestionAction): SuggestionPick | null {
  const other = value.other.trim();
  if (value.choice === 'skip') return { action: 'no', note: other };
  if (value.choice) return { action: value.choice, note: other };
  const picked = value.picked.filter((p) => p !== YES_OPTION);
  if (!value.picked.length && !other) return null;
  return { action: answer, note: [...picked, ...(other ? [other] : [])].join('; ') };
}

/** An action picked for it elsewhere (Jev's read of a reply upstream, T-58) as what's picked on the card. */
function valueOfAction(s: FactorySuggestion, action: SuggestionAction): DecisionValue {
  if (action === 'no') return { picked: [], other: '', choice: 'skip' };
  if (action === 'backlog' || action === 'branch') return { picked: [], other: '', choice: action };
  return { picked: [s.options?.length ? (s.recommended ?? s.options[0].label) : YES_OPTION], other: '' };
}

/** T-14: in the comments, the AI builder's suggestions as one line pointing to the task's Suggestions, where they're decided. */
export function FactorySuggestionsPointer({
  count,
  onShow,
}: {
  count: number;
  /** Scrolls to the suggestions (showTaskSuggestions). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <Lightbulb className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Suggested a further change' : `Suggested ${count} further changes`}</span>
      <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={onShow}>
        See Suggestions
      </Button>
    </div>
  );
}
